import { PageMeta } from './common';

/**
 * Notifications contract (T111, frozen). Persisted, real-time notifications delivered over a
 * WebSocket push with a REST backfill as the no-loss guarantee. See
 * `docs/requirements/13-notifications.md` (T110) and `docs/architecture/notifications-design.md`.
 *
 * The server stores `type` + structured `payload`; the human sentence is composed CLIENT-SIDE from
 * `type` + `payload` via i18n (DEC-NOT-M1) — never a pre-rendered string on the wire.
 *
 * The `type` set is an APPLICATION-level enum (validated in code); it is stored as a plain string
 * column so new trigger types are additive with no schema migration (see design decision D3).
 */

/**
 * Notification type discriminator. The type set is additive (stored as a plain string column, no
 * migration per new type). 2026-07 adds the exam approval workflow pair:
 *  - `exam_approval_requested` — fans out to every active Admin when a sales role requests an exam.
 *  - `exam_approval_decided`   — to the original requester when an Admin approves or rejects it.
 */
export const NOTIFICATION_TYPES = [
  'lead_assigned',
  'exam_approval_requested',
  'exam_approval_decided',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/** The kinds of entity a notification can point at (for deep-link navigation). */
export const RELATED_ENTITY_TYPES = ['lead', 'exam_booking'] as const;
export type RelatedEntityType = (typeof RELATED_ENTITY_TYPES)[number];

/**
 * A retrievable reference to the domain entity a notification is "about", enabling navigation.
 * For `lead_assigned` this is `{ entity: 'lead', id: <leadId> }` → the FE routes to `/leads/:id`.
 */
export interface RelatedEntityRef {
  entity: RelatedEntityType;
  id: string;
}

/**
 * Payload for `lead_assigned`. The base sentence ("A lead ({{leadName}}) was assigned to you")
 * needs no server text — but `leadName` is snapshotted at creation so the panel entry stays
 * readable even if the lead is later soft-deleted (T110 §7), and navigation uses `leadId`.
 * `assignedByName` is an optional display nicety ("… by {{assignedByName}}").
 */
export interface LeadAssignedPayload {
  leadId: string;
  /** Lead's display name snapshotted at assignment time — resilient to later lead deletion. */
  leadName: string;
  /** Full name of the user who performed the assignment (optional display only). */
  assignedByName?: string;
}

/** Payload for `exam_approval_requested` (delivered to every active Admin). Names are snapshotted so
 * the panel entry stays readable regardless of later record changes; navigation uses `studentCode`. */
export interface ExamApprovalRequestedPayload {
  studentCode: string;
  studentName: string;
  examSection: string;
  /** Full name of the sales user who requested the exam (display only). */
  requestedByName: string;
}

/** Payload for `exam_approval_decided` (delivered to the original requester). */
export interface ExamApprovalDecidedPayload {
  studentCode: string;
  studentName: string;
  examSection: string;
  decision: 'approved' | 'rejected';
  /** Full name of the Admin who decided (display only). */
  decidedByName: string;
  /** Optional rejection reason (present only when `decision === 'rejected'`). */
  reason?: string;
}

/** Maps each notification type to its payload shape. New types extend this map (additive). */
export interface NotificationPayloadMap {
  lead_assigned: LeadAssignedPayload;
  exam_approval_requested: ExamApprovalRequestedPayload;
  exam_approval_decided: ExamApprovalDecidedPayload;
}

/** Union of all payload shapes. */
export type NotificationPayload = NotificationPayloadMap[NotificationType];

/**
 * openapi.yaml components.schemas.NotificationItem — the single DTO shared by the REST list,
 * the single-read response, and the WS `notification:new` push. `readAt === null` ⇒ unread.
 */
export interface NotificationItem<T extends NotificationType = NotificationType> {
  id: string;
  type: T;
  payload: NotificationPayloadMap[T];
  /** Navigation target; null only for future types that carry no entity ref. */
  relatedEntity: RelatedEntityRef | null;
  /** ISO datetime the notification was marked read; null while unread. */
  readAt: string | null;
  /** ISO datetime of creation (also the delivery/event time). */
  createdAt: string;
}

/** Query for `GET /notifications` (all fields optional; own-scope is implicit, never a param). */
export interface NotificationListQuery {
  page?: number;
  pageSize?: number;
  /** When true, return only unread (`readAt IS NULL`) rows — used by the reconnect backfill. */
  unreadOnly?: boolean;
}

/**
 * openapi.yaml components.schemas.NotificationListResponse — `GET /notifications`.
 * Carries `unreadCount` so one call feeds BOTH the panel list and the badge (server-derived count).
 */
export interface NotificationListResponse {
  data: NotificationItem[];
  meta: PageMeta;
  /** Server-derived total unread for the caller (exact; the FE caps the *display* at "9+"). */
  unreadCount: number;
}

/** openapi.yaml components.schemas.UnreadCountResponse — `GET /notifications/unread-count`. */
export interface UnreadCountResponse {
  unreadCount: number;
}

/**
 * openapi.yaml components.schemas.MarkReadResponse — `PATCH /notifications/:id/read`.
 * Returns the updated item plus the fresh unread count so the badge reconciles without a 2nd call.
 * Idempotent: re-reading an already-read row returns it unchanged (no error).
 */
export interface MarkReadResponse {
  notification: NotificationItem;
  unreadCount: number;
}

/** openapi.yaml components.schemas.MarkAllReadResponse — `POST /notifications/read-all`. */
export interface MarkAllReadResponse {
  /** How many rows transitioned from unread → read by this call. */
  updated: number;
  /** Always 0 after a successful mark-all-read. */
  unreadCount: number;
}

/* ------------------------------------------------------------------ WebSocket channel contract */
/* The WS surface is intentionally NOT in the REST OpenAPI (documented in notifications-design.md). */

/** socket.io namespace the notifications channel is served on (shares the HTTP server/port). */
export const NOTIFICATIONS_WS_NAMESPACE = '/notifications';

/** Server → client event names. */
export const NOTIFICATION_WS_EVENTS = {
  /** A new notification for the connected user. */
  new: 'notification:new',
  /** An unread-count-only update (e.g. after read/mark-all-read from another session). */
  unreadCount: 'notification:unread-count',
} as const;

/** Payload of the `notification:new` server→client event. */
export interface NotificationNewEvent {
  notification: NotificationItem;
  /** The recipient's unread count immediately after this notification was persisted. */
  unreadCount: number;
}

/** Payload of the `notification:unread-count` server→client event. */
export interface NotificationUnreadCountEvent {
  unreadCount: number;
}
